import React from "react";
import { Helmet } from "react-helmet-async";
import { Copy, Monitor, Wand2, Sparkles, Gift, Ticket, Lock } from "lucide-react";
import Logo from "@/components/Logo";

const surveyLink = "https://forms.gle/D4SC2zbovrmFHCoi7"; // Updated survey link

const Landing: React.FC = () => {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(surveyLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-sauna-beige flex flex-col items-center px-4 pb-12">
      <Helmet>
        <title>Sauna Operator Survey | SaunaDip</title>
        <meta name="description" content="Help shape the future of sauna tech and get rewarded. Take our 2-minute survey for sauna operators." />
      </Helmet>

      {/* Hero Section */}
      <header className="w-full max-w-md mx-auto text-center pt-8 pb-6">
        <div className="flex justify-center mb-4">
          <span className="text-4xl font-bold">
            <span className="text-sauna-orange">Sauna</span>
            <span className="text-sauna-blue">Dip</span>
          </span>
        </div>
        <h1 className="text-2xl font-bold text-sauna-navy mb-3 leading-tight">
          🧖 Sauna Operator Survey
        </h1>
        <p className="text-xl text-sauna-navy/80 mb-4">
          Help shape the future of sauna tech — and get rewarded.
        </p>
      </header>

      {/* Platform Features */}
      <section className="w-full max-w-md mx-auto mb-8">
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-bold text-sauna-navy mb-4">We're building a new platform designed just for sauna operators:</h2>
          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <Monitor className="w-5 h-5 text-sauna-orange mt-1 flex-shrink-0" />
              <span className="text-sauna-navy">Monitor your sauna and plunge temps remotely</span>
            </li>
            <li className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-sauna-orange mt-1 flex-shrink-0" />
              <span className="text-sauna-navy">Get alerts, trends, and usage insights</span>
            </li>
            <li className="flex items-start gap-3">
              <Wand2 className="w-5 h-5 text-sauna-orange mt-1 flex-shrink-0" />
              <span className="text-sauna-navy">Reduce stress and save time every day</span>
            </li>
          </ul>
        </div>
      </section>

      {/* Survey Info */}
      <section className="w-full max-w-md mx-auto mb-8 text-center">
        <p className="text-lg text-sauna-navy mb-2">
          Your feedback helps us build what you need.
        </p>
        <p className="text-sauna-navy/80">
          👉 It only takes 2 minutes to complete.
        </p>
      </section>

      {/* Rewards Section */}
      <section className="w-full max-w-md mx-auto mb-8">
        <h2 className="text-lg font-bold text-sauna-navy mb-4 text-center">🎁 Choose Your Reward</h2>
        <p className="text-center text-sauna-navy/80 mb-6">Fill out the survey and pick your reward:</p>
        
        <div className="space-y-4">
          <div className="bg-white rounded-xl p-4 flex items-start gap-4 shadow-sm">
            <Ticket className="w-6 h-6 text-sauna-orange mt-1 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-sauna-navy">Win a free listing on SaunaDip for 3 months</h3>
              <p className="text-sm text-sauna-navy/70">Get premium visibility for your sauna business</p>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-4 flex items-start gap-4 shadow-sm">
            <Wand2 className="w-6 h-6 text-sauna-orange mt-1 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-sauna-navy">Early invite to test the platform with your setup</h3>
              <p className="text-sm text-sauna-navy/70">Be among the first to try our monitoring system</p>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-4 flex items-start gap-4 shadow-sm">
            <Gift className="w-6 h-6 text-sauna-orange mt-1 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-sauna-navy">Sticker pack or exclusive SaunaDip hat</h3>
              <p className="text-sm text-sauna-navy/70">Complete & share to get your swag</p>
            </div>
          </div>
        </div>
      </section>

      {/* Survey Form */}
      <section id="survey" className="w-full max-w-md mx-auto mb-8">
        <div className="text-center mb-6">
          <a 
            href={surveyLink} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="inline-block px-8 py-4 rounded-lg bg-sauna-orange text-white font-semibold shadow hover:bg-sauna-blue transition-colors text-lg"
          >
            Take the Survey →
          </a>
        </div>
        
        <div className="bg-white rounded-xl p-4 flex items-center gap-3 shadow-sm">
          <Lock className="w-5 h-5 text-sauna-orange" />
          <p className="text-sm text-sauna-navy/70">
            🔒 Your info is safe. We'll never share your email or personal details — it's only used to follow up on your reward or platform access.
          </p>
        </div>
      </section>

      {/* Share Section */}
      <section className="w-full max-w-md mx-auto text-center mt-8">
        <p className="text-base text-sauna-navy/70 mb-4">Know other sauna operators who might be interested? Share this with them!</p>
        <div className="flex items-center justify-center gap-2 mb-2">
          <span className="bg-white px-3 py-2 rounded-lg text-sauna-navy text-sm font-mono border border-sauna-blue select-all">{surveyLink}</span>
          <button onClick={handleCopy} className="p-2 bg-sauna-orange rounded-lg text-white hover:bg-sauna-blue transition-colors" aria-label="Copy link">
            <Copy className="w-5 h-5" />
          </button>
        </div>
        {copied && <span className="text-sauna-green text-sm">Link copied!</span>}
      </section>
    </div>
  );
};

export default Landing; 